import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import GeneralForm from '../../components/generalForm';
import { useDeliveryDayMarketplace, useDeliveryDayMarketplaceEdit, useItems } from './apiHooks';
import utils from '../../utils/utils';
import DateField from '../../components/customFields/dateField';
import MarketplacePredictedOrderItems from '../../components/customFields/deliveryDayMarketplacePredictedOrderItems';
import DATE_FORMAT from '../../components/constants/dateFormat';

function DeliveryDayMarketplaceForm({ intl, match, history }) {
  const [formDeliveryDayMarketplace, setFormDeliveryDayMarketplace] = useState({
    predictedOrderItems: [],
  });

  const {
    isLoading: loading,
    isError,
    error,
    deliveryDayMarketplace,
  } = useDeliveryDayMarketplace({
    deliveryDayMarketplaceId: match.params.deliveryDayMarketplaceId,
    onSuccess: (ddm) =>
      setFormDeliveryDayMarketplace({
        id: ddm.id,
        deadlineDay: ddm.deadline,
        deadlineHour: utils.getFormattedDateFromString(ddm.deadline, 'H'),
        deadlineMinutes: utils.getFormattedDateFromString(ddm.deadline, 'm'),
        totalSumLimit: ddm.totalSumLimit,
        predictedOrderItems: ddm.predictedOrderItems.map((poi) => ({
          id: poi.id,
          predictedQuantity: `${poi.predictedQuantity}`,
          itemId: poi.item.id,
        })),
      }),
  });

  const { isLoading: itemsLoading, result: itemsResult } = useItems({
    marketplaceId: deliveryDayMarketplace?.marketplaceId,
  });

  const {
    editDeliveryDayMarketplace,
    isLoading: editLoading,
    isError: isEditError,
    error: deliveryDayMarketplaceEditError,
  } = useDeliveryDayMarketplaceEdit(() =>
    history.push({
      pathname: '/admin/deliveryDayMarketplaces',
    })
  );

  const formSchema = {
    type: 'object',
    required: [],
    properties: {
      deadlineDay: {
        type: 'string',
        title: intl.formatMessage({ id: 'delivery.day.marketplace.form.field.deadline.day.label' }),
      },
      deadlineHour: {
        type: 'string',
        title: intl.formatMessage({ id: 'delivery.day.marketplace.form.field.deadline.hour.label' }),
      },
      deadlineMinutes: {
        type: 'string',
        title: intl.formatMessage({ id: 'delivery.day.marketplace.form.field.deadline.minutes.label' }),
      },
      totalSumLimit: {
        type: 'string',
        title: intl.formatMessage({ id: 'delivery.day.marketplace.form.field.limit.label' }),
      },
      resetOrderEmail: {
        type: 'boolean',
        title: intl.formatMessage({ id: 'delivery.day.marketplace.form.field.reset.order.email.label' }),
      },
      predictedOrderItems: {
        type: 'array',
        items: itemsResult?.items || [],
      },
    },
  };

  const uiSchema = {
    deadlineDay: {
      'ui:field': 'dateField',
    },
    predictedOrderItems: {
      'ui:field': 'marketplacePredictedOrderItems',
    },
    resetOrderEmail: {
      'ui:help': intl.formatMessage({ id: 'delivery.day.marketplace.form.field.reset.order.email.help' }),
    },
  };

  const onFormSubmit = () => {
    try {
      editDeliveryDayMarketplace({ deliveryDayMarketplace: formDeliveryDayMarketplace });
    } catch (e) {
      window.scrollTo(0, 0);
    }
  };

  const formFields = {
    dateField: DateField,
    marketplacePredictedOrderItems: MarketplacePredictedOrderItems,
  };

  const buttonCopy = 'delivery.day.marketplace.edit.form.submit';

  return (
    <div className="row">
      <div className="col-12 col-lg-10 offset-lg-1 admin__form">
        {deliveryDayMarketplace ? (
          <h2>{`${deliveryDayMarketplace.marketplaceName} - ${utils.getFormattedDateFromString(
            deliveryDayMarketplace.deliveryDate,
            DATE_FORMAT.displayWeekdayAndDate
          )}`}</h2>
        ) : (
          ''
        )}

        <GeneralForm
          onChange={(event) => setFormDeliveryDayMarketplace(event.formData)}
          onSubmit={onFormSubmit}
          formSchema={formSchema}
          formUiSchema={uiSchema}
          formFields={formFields}
          formData={formDeliveryDayMarketplace}
          buttonCopy={buttonCopy}
          errorMessage={
            isError || isEditError
              ? utils.getErrorMessageFromResponse(error || deliveryDayMarketplaceEditError, true)
              : null
          }
          loading={loading || editLoading || itemsLoading}
          hideImageUpload={true}
        />
      </div>
    </div>
  );
}

DeliveryDayMarketplaceForm.propTypes = {
  onSuccess: PropTypes.func.isRequired,
};

DeliveryDayMarketplaceForm.defaultProps = {
  onSuccess: () => {},
};

function mapStateToProps(state) {
  return {
    currentUser: state.currentUser.user,
  };
}

const mapDispatchToProps = {};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(DeliveryDayMarketplaceForm));
